import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Credit cost per generation action, read from `generation_costs`.
 *
 * The table is the single source of truth so pricing can be adjusted from the
 * backoffice without a deploy. Previously these numbers were hardcoded in the
 * frontend, which meant the table and the app could disagree — and they did.
 *
 * Fallbacks below are only used if the table can't be reached; they mirror the
 * seeded values so a network failure degrades to correct-looking pricing
 * rather than zero.
 */

export const FALLBACK_COSTS: Record<string, number> = {
  render_base: 20,
  render_edit: 10,
  video_lite: 30,
  video_fast: 50,
  video_standard: 90,
};

/** Flat rate for any channel format variant. */
export const FALLBACK_FORMAT_COST = 10;

/** Map a Veo model id to its generation_costs action key. */
export function videoActionKey(modelId: string): string {
  if (modelId.includes("lite")) return "video_lite";
  if (modelId.includes("fast")) return "video_fast";
  return "video_standard";
}

/** Map an aspect ratio ("16:9") to its format action key ("format_16x9"). */
// Re-exported so existing imports keep working; the derivation itself lives
// with the edge functions, imported by relative path the same way
// promptAssembly and geminiFailure already are. This function used to be a
// local copy that disagreed with the server: it turned "5K" into format_5K —
// not a row, so the tile fell back to showing 10 credits — while the server
// charged the real upscale_5k price of 25. Quoted one number, billed another.
export { deliverableActionKey as formatActionKey } from "../../../../supabase/functions/_shared/rateCard.ts";

export function useGenerationCosts() {
  const [costs, setCosts] = useState<Record<string, number>>(FALLBACK_COSTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("generation_costs" as any)
        .select("action_key, credits")
        .eq("is_active", true);
      if (cancelled) return;
      if (!error && data) {
        const map: Record<string, number> = {};
        for (const row of data as any[]) map[row.action_key] = row.credits;
        setCosts((prev) => ({ ...prev, ...map }));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  /** Credits for an action key, falling back to a sensible default. */
  const costOf = (actionKey: string, fallback = 0): number =>
    costs[actionKey] ?? FALLBACK_COSTS[actionKey] ?? fallback;

  return { costs, costOf, loading };
}
